import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { Card, Button, Space, Divider, message } from 'antd';
import pageS from '../../s.module.less';
import s from './s.module.less';
import BillingPoliciesStep from '../BillingPoliciesStep';
import ConsentFormsStep from '../ConsentFormsStep';
import PracticeProfileStep from '../PracticeProfileStep';
import BusinessInfoStep from '../BusinessInfoStep';
import PayoutAccountStep from '../PayoutAccountStep';
import { BusinessType } from 'types/paymentSetup';

const ReviewStep: React.FC = forwardRef((Props, ref) => {
    const [editingSection, setEditingSection] = useState<number | null>(null);
    const billingPoliciesRef = useRef<any>(null);
    const consentFormsRef = useRef<any>(null);
    const practiceAddressRef = useRef<any>(null);
    const practiceProfileRef = useRef<any>(null);
    const businessInfoRef = useRef<any>(null);
    const payoutAccountRef = useRef<any>(null);

    const steps = [
        { id: 1, title: 'Appointment billing policies', component: <BillingPoliciesStep ref={billingPoliciesRef} isReview isEdit={editingSection === 1} /> },
        { id: 2, title: 'Practice profile', component: <PracticeProfileStep ref={practiceProfileRef} isReview isEdit={editingSection === 2} showPracticeAddress={false} /> },
        { id: 3, title: 'Consent forms', component: <ConsentFormsStep ref={consentFormsRef} isReview isEdit={editingSection === 3} /> },
        { id: 4, title: 'Practice Address', component: <PracticeProfileStep ref={practiceAddressRef} isReview isEdit={editingSection === 4} showPracticeForm={false} /> },
        { id: 5, title: 'Business information and Business identity verification', component: <BusinessInfoStep ref={businessInfoRef} isReview isEdit={editingSection === 5} /> },
        { id: 6, title: 'Payout account', component: <PayoutAccountStep ref={payoutAccountRef} isReview isEdit={editingSection === 6} /> },
    ];

    const handleCancel = () => {
        setEditingSection(null);
    };

    const handleSave = async (sectionId: number) => {
        try {
            switch (sectionId) {
                case 1:
                    await billingPoliciesRef.current?.onSubmit();
                    break;
                case 2:
                    await consentFormsRef.current?.onSubmit();
                    break;
                case 3:
                    await practiceProfileRef.current?.onSubmit();
                    break;
                case 4:
                    await practiceAddressRef.current?.onSubmit();
                    break;
                case 5:
                    await businessInfoRef.current?.onSubmit();
                    break;
                case 6:
                    await payoutAccountRef.current?.onSubmit();
                    break;
                default:
                    setEditingSection(null);
                    break;
            }
            setEditingSection(null);
        } catch (error) {
            console.error(error);
        }
    };

    const handleSubmit = () => {
        const businessType = businessInfoRef.current?.getBusinessType?.();
        if (!businessType) {
            message.error('Business type is required. Please select a specific type to submit.');
            throw new Error('Business type error');
        }
        if (businessType === BusinessType.OTHERS) {
            message.error('Business type cannot be "Others" at this time. Please select a specific type to submit.');
            throw new Error('Business type error');
        }
    };

    useImperativeHandle(ref, () => ({
        onSubmit: handleSubmit,
    }));

    const renderEditButton = (sectionId: number) => (
        <Button
            onClick={() => setEditingSection(sectionId)}
            className={s.editButton}
        >
            Edit
        </Button>
    );

    const renderActionButtons = (sectionId: number) => (
        <Space>
            <Button
                onClick={handleCancel}
            >
                Cancel
            </Button>
            <Button
                type="primary"
                onClick={() => handleSave(sectionId)}
                className={s.saveButton}
            >
                Save changes
            </Button>
        </Space>
    );

    return (
        <div className={s.reviewStep}>
            <div className={s.header}>
                <h1 className={pageS.title}>Review your setup</h1>
                <p className={pageS.desc}>Please confirm your practice details before moving forward. You&rsquo;ll be able to make changes later if needed.</p>
            </div>

            <div className={s.sections}>
                {
                    steps.map((step) => (
                        <Card
                            key={step.id}
                            title={step.title}
                            extra={editingSection === step.id ? renderActionButtons(step.id) : renderEditButton(step.id)}
                            className={s.sectionCard}
                            bordered={false}
                        >
                            <Divider style={{ marginTop: '8px' }} />
                            {step.component}
                        </Card>
                    ))
                }
            </div>
        </div>
    );
});

export default ReviewStep;
